import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-picture-modal',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './picture-modal.component.html',
  styleUrl: './picture-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PictureModalComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly document = inject(DOCUMENT);

  @ViewChild('backdrop') backdropRef?: ElementRef<HTMLElement>;

  @Input() src: string | null = null;
  @Input() alt = 'Billede';

  isOpen = false;
  displaySrc: string | null = null;
  displayAlt = 'Billede';

  private savedBodyOverflow = '';

  open(src: string, alt?: string): void {
    if (!src) {
      return;
    }
    this.displaySrc = src;
    this.displayAlt = alt || this.alt || 'Billede';
    if (this.isOpen) {
      this.cdr.markForCheck();
      return;
    }
    this.isOpen = true;
    this.cdr.detectChanges();
    this.mountBackdropInViewport();
  }

  close(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.restoreBodyScroll();
    this.cdr.markForCheck();
  }

  onBackdropClick(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).classList.contains('picture-modal-backdrop')
    ) {
      this.close(event);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  private mountBackdropInViewport(): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop) {
      return;
    }

    if (backdrop.parentElement !== this.document.body) {
      this.document.body.appendChild(backdrop);
    }

    backdrop.style.position = 'fixed';
    backdrop.style.inset = '0';
    backdrop.style.width = '100vw';
    backdrop.style.height = '100vh';
    backdrop.style.zIndex = '10000';
    backdrop.style.display = 'flex';
    backdrop.style.alignItems = 'center';
    backdrop.style.justifyContent = 'center';

    this.savedBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    this.document.body.style.overflow = this.savedBodyOverflow;
  }

  ngOnDestroy(): void {
    this.restoreBodyScroll();
    const backdrop = this.backdropRef?.nativeElement;
    if (backdrop?.parentElement === this.document.body) {
      backdrop.remove();
    }
  }
}
